import { useEffect, useState } from 'preact/hooks'
import { urlFromShare } from '../shared/clean'
import type { ResolveResult } from '../shared/types'
import { buzz, copy, pushHistory, resolve, tryCloseWindow } from './lib'
import { Result } from './Result'

type Phase =
  | { kind: 'working' }
  | { kind: 'done'; result: ResolveResult } // 已複製，視窗關不掉
  | { kind: 'tap'; result: ResolveResult } // 需要使用者手勢才能寫剪貼簿
  | { kind: 'error'; message: string }

/**
 * Share target 落地頁。
 *
 * 目標動線是「完全不需要互動」：解析 → 複製 → 關閉視窗。
 * 但兩個環節 web 平台都不保證，所以每一步都要有可見的 fallback：
 *   - 剪貼簿：沒有 user activation 時會被拒 → 換成滿版按鈕
 *   - 關視窗：window.close() 不一定生效 → 顯示完成狀態讓使用者自己滑掉
 */
export function Share() {
  const [phase, setPhase] = useState<Phase>({ kind: 'working' })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const target = urlFromShare(params)

    if (!target) {
      setPhase({ kind: 'error', message: '分享的內容裡找不到網址' })
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const result = await resolve(target)
        if (cancelled) return
        pushHistory({ input: target, clean: result.clean })

        const outcome = await copy(result.clean)
        if (cancelled) return

        if (outcome !== 'copied') {
          setPhase({ kind: 'tap', result })
          return
        }

        buzz()
        const closed = await tryCloseWindow()
        if (!cancelled && !closed) setPhase({ kind: 'done', result })
      } catch (err) {
        if (!cancelled) {
          setPhase({
            kind: 'error',
            message: err instanceof Error ? err.message : '解析失敗',
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  if (phase.kind === 'working') {
    return (
      <Center>
        <Spinner />
        <p class="mt-4 text-sm text-slate-500 dark:text-slate-400">正在還原…</p>
      </Center>
    )
  }

  if (phase.kind === 'error') {
    return (
      <Center>
        <p class="text-3xl">⚠️</p>
        <p class="mt-3 text-sm text-slate-600 dark:text-slate-300">{phase.message}</p>
        <a href="/" class="btn btn-ghost mt-5 text-xs">
          回到 deShorten
        </a>
      </Center>
    )
  }

  // 需要手勢才能複製：整個畫面就是一顆按鈕，按完直接複製並嘗試關閉。
  if (phase.kind === 'tap') {
    return (
      <Center>
        <button
          type="button"
          onClick={async () => {
            const outcome = await copy(phase.result.clean)
            if (outcome === 'copied') {
              buzz()
              const closed = await tryCloseWindow()
              if (!closed) setPhase({ kind: 'done', result: phase.result })
            }
          }}
          class="btn btn-primary w-full px-6 py-5 text-base"
        >
          點一下複製純網址
        </button>
        <p class="url mt-4 max-w-sm text-center text-slate-500 dark:text-slate-400">
          {phase.result.clean}
        </p>
      </Center>
    )
  }

  return (
    <main class="mx-auto w-full max-w-md px-4 py-10">
      <div class="mb-6 text-center">
        <p class="text-4xl">✓</p>
        <h1 class="mt-3 text-base font-semibold">已複製到剪貼簿</h1>
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          這個視窗沒辦法自動關閉，直接滑掉回到剛剛的 App 就好。
        </p>
      </div>
      <Result result={phase.result} />
      <div class="mt-5 flex justify-center gap-2">
        <button type="button" onClick={() => void tryCloseWindow()} class="btn btn-ghost text-xs">
          關閉
        </button>
        <a href="/" class="btn btn-ghost text-xs">
          再還原一個
        </a>
      </div>
    </main>
  )
}

function Center({ children }: { children: preact.ComponentChildren }) {
  return (
    <main class="flex min-h-[80vh] flex-col items-center justify-center px-6 text-center">
      {children}
    </main>
  )
}

function Spinner() {
  return (
    <div
      class="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500 dark:border-white/15 dark:border-t-cyan-400"
      role="status"
      aria-label="載入中"
    />
  )
}
